/*
 * ============================================================
 * CCS STORE FORM — assets/ccs-store-form.js
 * ============================================================
 * Wires snippets/ccs-store-form.liquid, for both registering and editing.
 *
 *   CCSStoreForm.mount(formEl, { store, onSaved, onCancel })
 *
 * `store` null means registration; a store object means editing, and the form
 * pre-fills and switches its verbs.
 */
(function (window, document) {
  'use strict';

  var HANDLE_DEBOUNCE_MS = 350;

  function $(root, selector) { return root.querySelector(selector); }

  function mount(form, options) {
    var opts = options || {};
    var store = opts.store || null;
    var editing = !!store;

    var nameEl = $(form, '[name="name"]');
    var handleEl = $(form, '[name="handle"]');
    var descEl = $(form, '[name="description"]');
    var siteEl = $(form, '[name="ownerWebsite"]');
    var logoEl = $(form, '[name="logoImageUrl"]');
    var statusEl = $(form, '.ccs-handle-status');
    var errorEl = $(form, '.ccs-form-error');
    var submitEl = $(form, '[data-ccs="submit"]');
    var submitLabel = $(form, '[data-ccs="submit-label"]');
    var cancelEl = $(form, '[data-ccs="cancel"]');
    var fileEl = $(form, 'input[type="file"]');
    var preview = $(form, '.ccs-logo-preview');

    /*
     * Once the seller edits the URL themselves it stops following the name.
     * Without this, typing the name after setting a custom URL would quietly
     * overwrite their choice. An existing store starts "touched" for the same
     * reason -- their URL is already decided.
     */
    var handleTouched = editing;
    var handleTimer = null;
    var lastChecked = null;

    if (editing) {
      nameEl.value = store.name || '';
      handleEl.value = store.handle || '';
      descEl.value = store.description || '';
      siteEl.value = store.ownerWebsite || '';
      logoEl.value = store.logoImageUrl || '';
      showLogo(store.logoImageUrl);
      submitLabel.textContent = 'Save changes';
      if (cancelEl && opts.onCancel) cancelEl.hidden = false;
    }

    // ── Store URL ───────────────────────────────────────────────────────────

    function setStatus(state, message) {
      statusEl.dataset.state = state;
      statusEl.textContent = message || '';
    }

    function checkHandle() {
      var value = handleEl.value.trim();
      if (!value) { setStatus('idle', ''); return; }
      if (value === lastChecked) return;
      if (editing && value === store.handle) {
        setStatus('ok', 'This is your current store URL.');
        return;
      }

      lastChecked = value;
      setStatus('checking', 'Checking…');

      window.CCSApi.checkHandle(value).then(function (result) {
        // A slower earlier check can land after a later one; ignore anything
        // that is no longer what the field says.
        if (handleEl.value.trim() !== value) return;
        if (result.available) {
          setStatus('ok', 'chromecityshirts.com/' + result.handle + ' is available.');
        } else if (result.suggestion) {
          setStatus('taken', (result.error || 'Taken.') + ' Try ' + result.suggestion + '.');
        } else {
          setStatus('taken', result.error || 'That URL is not available.');
        }
      }).catch(function () {
        // A failed check must not block submission -- the server validates
        // again on save, which is the check that actually decides.
        setStatus('idle', '');
      });
    }

    function queueCheck() {
      clearTimeout(handleTimer);
      handleTimer = setTimeout(checkHandle, HANDLE_DEBOUNCE_MS);
    }

    nameEl.addEventListener('input', function () {
      if (handleTouched) return;
      handleEl.value = window.CCSApi.slugify(nameEl.value);
      queueCheck();
    });

    handleEl.addEventListener('input', function () {
      handleTouched = true;
      queueCheck();
    });

    // Normalise on the way out so the seller sees what will actually be used.
    handleEl.addEventListener('blur', function () {
      var cleaned = window.CCSApi.slugify(handleEl.value);
      if (cleaned !== handleEl.value) {
        handleEl.value = cleaned;
        lastChecked = null;
        checkHandle();
      }
    });

    // ── Logo ────────────────────────────────────────────────────────────────

    function showLogo(url) {
      var img = $(preview, 'img');
      var clearBtn = $(form, '[data-ccs="clear-logo"]');
      if (url) {
        img.src = url;
        img.hidden = false;
        preview.dataset.empty = 'false';
        $(preview, 'span').hidden = true;
        if (clearBtn) clearBtn.hidden = false;
      } else {
        img.hidden = true;
        img.removeAttribute('src');
        preview.dataset.empty = 'true';
        $(preview, 'span').hidden = false;
        if (clearBtn) clearBtn.hidden = true;
      }
    }

    form.addEventListener('click', function (event) {
      var action = event.target.closest('[data-ccs]');
      if (!action) return;
      var kind = action.getAttribute('data-ccs');
      if (kind === 'pick-logo') { fileEl.click(); }
      if (kind === 'clear-logo') { logoEl.value = ''; showLogo(''); }
      if (kind === 'cancel' && opts.onCancel) { event.preventDefault(); opts.onCancel(); }
    });

    fileEl.addEventListener('change', function () {
      var file = fileEl.files && fileEl.files[0];
      if (!file) return;
      setBusy(true, 'Uploading…');
      window.CCSUpload.image(file).then(function (url) {
        logoEl.value = url;
        showLogo(url);
        setBusy(false);
      }).catch(function (error) {
        setBusy(false);
        showError(error.message || 'That image could not be uploaded.');
      });
    });

    // ── Submit ──────────────────────────────────────────────────────────────

    function showError(message) {
      errorEl.textContent = message;
      errorEl.hidden = !message;
      if (message) errorEl.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    }

    function setBusy(busy, label) {
      submitEl.disabled = busy;
      submitLabel.textContent = busy
        ? (label || 'Saving…')
        : (editing ? 'Save changes' : 'Create my store');
    }

    form.addEventListener('submit', function (event) {
      event.preventDefault();
      showError('');

      var fields = {
        name: nameEl.value.trim(),
        handle: handleEl.value.trim(),
        description: descEl.value.trim(),
        ownerWebsite: siteEl.value.trim(),
        logoImageUrl: logoEl.value.trim()
      };

      if (!fields.name) {
        showError('Your store needs a name.');
        nameEl.focus();
        return;
      }

      setBusy(true);
      var save = editing ? window.CCSApi.updateStore(fields) : window.CCSApi.createStore(fields);

      save.then(function (data) {
        setBusy(false);
        if (opts.onSaved) opts.onSaved(data.store, data);
      }).catch(function (error) {
        setBusy(false);
        showError(error.message);
        // The server hands back a free alternative when a URL is taken; putting
        // it in the field is faster than making them invent another.
        var suggestion = error.payload && error.payload.suggestion;
        if (suggestion) {
          handleEl.value = suggestion;
          handleTouched = true;
          lastChecked = null;
          checkHandle();
        }
      });
    });

    if (editing) checkHandle();

    return {
      form: form,
      focus: function () { nameEl.focus(); }
    };
  }

  window.CCSStoreForm = { mount: mount };
})(window, document);
